import React from 'react';
import { Input, DatePicker, Button, Tag } from 'antd';
import dayjs from 'dayjs';

interface Page1TabProps {
  formData: Record<string, unknown>;
  isEditing: boolean;
  isApproved: boolean;
  getField: (key: string) => unknown;
  setField: (key: string, value: unknown) => void;
  onOpenWashLabel: () => void;
  detailCount: number;
}

const Page1Tab: React.FC<Page1TabProps> = React.memo(({
  formData, isEditing, isApproved, getField, setField, onOpenWashLabel, detailCount,
}) => {
  const str = (k: string): string => (getField(k) as string) || '';
  const num = (k: string): number => (getField(k) as number) || 0;
  const dateVal = (k: string) => {
    const v = getField(k) as string;
    return v ? dayjs(v) : null;
  };
  // When approved, only fj740_77 and fj740_78 are editable
  const canEdit = isEditing && !isApproved;
  const canEditDate = isEditing; // fj740_77/78 always editable when editing

  return (
    <div className="j219-page-content">
      {isApproved && (
        <Tag color="green" style={{ marginBottom: 12, fontSize: 14 }}>已核准</Tag>
      )}

      {/* Row 1: 業務分析單號 */}
      <div className="j219-form-row">
        <div className="j219-field">
          <label>業務分析單號</label>
          <Input value={str('fj740_76')} disabled data-contract-field="fj740_76" />
        </div>
      </div>

      {/* Row 2: 開發單號 / 產品用途 */}
      <div className="j219-form-row">
        <div className="j219-field">
          <label>開發單號</label>
          <Input
            value={str('fj740_11')}
            disabled={!canEdit}
            maxLength={5}
            onChange={e => setField('fj740_11', e.target.value)} data-contract-field="fj740_11"
          />
        </div>
        <div className="j219-field">
          <label>產品用途</label>
          <Input
            value={str('fj740_13')}
            disabled={!canEdit}
            maxLength={2}
            onChange={e => setField('fj740_13', e.target.value)} data-contract-field="fj740_13"
          />
        </div>
      </div>

      {/* Row 3: 廠商收件單號 / 項次 */}
      <div className="j219-form-row">
        <div className="j219-field">
          <label>廠商收件單號</label>
          <Input
            value={str('fj740_14')}
            disabled={!canEdit}
            maxLength={4}
            onChange={e => setField('fj740_14', e.target.value)} data-contract-field="fj740_14"
          />
        </div>
        <div className="j219-field j219-field-sm">
          <label>項次</label>
          <Input
            type="number"
            value={num('fj740_15')}
            disabled={!canEdit}
            onChange={e => setField('fj740_15', parseInt(e.target.value) || 0)} data-contract-field="fj740_15"
          />
        </div>
      </div>

      {/* Row 4: 產品代號 */}
      <div className="j219-form-row">
        <div className="j219-field j219-field-sm">
          <label>產品代號1</label>
          <Input
            value={str('fj740_161')}
            disabled={!canEdit}
            maxLength={6}
            onChange={e => setField('fj740_161', e.target.value)} data-contract-field="fj740_161"
          />
        </div>
        <div className="j219-field j219-field-sm">
          <label>產品代號2</label>
          <Input
            value={str('fj740_162')}
            disabled={!canEdit}
            maxLength={6}
            onChange={e => setField('fj740_162', e.target.value)} data-contract-field="fj740_162"
          />
        </div>
      </div>

      {/* Row 5: 規格 (memo) */}
      <div className="j219-form-row">
        <div className="j219-field j219-field-full">
          <label>產品規格</label>
          <Input.TextArea
            data-contract-field="fj740_17"
            value={str('fj740_17')}
            disabled={!canEdit}
            rows={3}
            onChange={e => setField('fj740_17', e.target.value)}
          />
        </div>
      </div>

      {/* Row 6: 布樣數量 / 單位 / 繳交研發處布樣數量 */}
      <div className="j219-form-row">
        <div className="j219-field">
          <label>布樣數量</label>
          <Input
            type="number"
            value={num('fj740_18')}
            disabled={!canEdit}
            onChange={e => setField('fj740_18', parseFloat(e.target.value) || 0)} data-contract-field="fj740_18"
          />
        </div>
        <div className="j219-field j219-field-sm">
          <label>單位</label>
          <Input
            value={str('fj740_19')}
            disabled={!canEdit}
            maxLength={3}
            onChange={e => setField('fj740_19', e.target.value)} data-contract-field="fj740_19"
          />
        </div>
        <div className="j219-field">
          <label>繳交研發處布樣數量</label>
          <Input
            type="number"
            value={num('fj740_181')}
            disabled={!canEdit}
            onChange={e => setField('fj740_181', parseFloat(e.target.value) || 0)} data-contract-field="fj740_181"
          />
        </div>
        <div className="j219-field j219-field-sm">
          <label>單位</label>
          <Input
            value={str('fj740_191')}
            disabled={!canEdit}
            maxLength={3}
            onChange={e => setField('fj740_191', e.target.value)} data-contract-field="fj740_191"
          />
        </div>
      </div>

      {/* Row 7: 入單人 / 部門 / 核准狀態 */}
      <div className="j219-form-row">
        <div className="j219-field">
          <label>入單人員</label>
          <Input value={str('fj740_62')} disabled data-contract-field="fj740_62" />
        </div>
        <div className="j219-field">
          <label>部門</label>
          <Input value={str('fj740_63')} disabled data-contract-field="fj740_63" />
        </div>
        <div className="j219-field j219-field-sm">
          <label>核准狀態</label>
          <Input value={str('fj740_80') === 'P' ? '已核准' : '未核准'} disabled data-contract-field="fj740_80" />
        </div>
      </div>

      {/* Row 8: 公告日期（核准後可編輯） */}
      <div className="j219-form-row">
        <div className="j219-field">
          <label>公告開始日</label>
          <DatePicker
            data-contract-field="fj740_77"
            style={{ width: '100%' }}
            value={dateVal('fj740_77')}
            disabled={!canEditDate}
            onChange={val => setField('fj740_77', val ? val.format('YYYY-MM-DD') : '')}
          />
        </div>
        <div className="j219-field">
          <label>公告終止日</label>
          <DatePicker
            data-contract-field="fj740_78"
            style={{ width: '100%' }}
            value={dateVal('fj740_78')}
            disabled={!canEditDate}
            onChange={val => setField('fj740_78', val ? val.format('YYYY-MM-DD') : '')}
          />
        </div>
      </div>

      {/* Row 9: Buttons */}
      <div className="j219-form-row" style={{ marginTop: 16 }}>
        <Button
          type="default"
          danger
          onClick={onOpenWashLabel}
          style={{ marginRight: 12 }}
        >
          洗標明細 ({detailCount})
        </Button>
      </div>
    </div>
  );
});

Page1Tab.displayName = 'Page1Tab';
export default Page1Tab;
